import Card from "@/components/Common/Card";
import HeaderBackButton from "@/components/HeaderBackButton";
import ContentLayout from "@/layouts/ContentLayout";
import {
  useReadScenarioApprovalsById,
  useUpdateScenarioApprovals,
} from "@/libs/react-query/hooks/useScenarioApprovalsQuery";
import { useParams, useLocation } from "react-router-dom";
import ProjectInfoIcon from "@/assets/images/BidDetail/project-info-icon.png";
import ManualProductionAnalysisIcon from "@/assets/images/BidDetail/manual-production-analytics-icon.png";
import TableIcon from "@/assets/images/table-icon.png";
import ApprovalDetailInfo from "./components/ApprovalDetailInfo";
import ApprovalDetailReasons from "./components/ApprovalDetailReasons";
import ApprovalDetailTable from "./components/ApprovalDetailTable";
import ApprovalAlertDialog from "../ApprovalResponse/components/ApprovalAlertDialog";
import { ScenarioDecision } from "@/consts/Scenario";
import { APPROVAL_RECEIVE_TYPE } from "@/consts/Approval";
import { cn } from "@/libs/utils";
import { useState, useMemo } from "react";
import { showToast } from "@/utils/toastUtils";
import useUserStore from "@/stores/User";

export default function ApprovalDetailPage() {
  const { approval_id = "" } = useParams();
  const location = useLocation();
  const [openAlert, setOpenAlert] = useState(false);
  const [dialogType, setDialogType] = useState<
    ScenarioDecision.APPROVED | ScenarioDecision.REJECTED
  >();

  const currentUser = useUserStore((state) => state.user);
  const { data } = useReadScenarioApprovalsById(approval_id);

  const passedMyApproval = location.state?.my_approval;
  const passedReceiveType = location.state?.receive_type;
  const { mutate: submitApproval } = useUpdateScenarioApprovals();

  const openDialog = (
    type: ScenarioDecision.APPROVED | ScenarioDecision.REJECTED
  ) => {
    setDialogType(type);
    setOpenAlert(true);
  };

  const handleSubmit = (reason: string) => {
    if (!approval_id || !dialogType) return;

    submitApproval(
      {
        approval_id: approval_id,
        data: { decision: dialogType, reason },
      },
      {
        onSuccess: () => {
          showToast({
            message: "승인 시나리오가 성공적으로 처리되었습니다.",
          });
        },
      }
    );

    setOpenAlert(false);
  };

  const selectedApproval = useMemo(() => data || null, [data]);

  if (!data) {
    return null;
  }

  return (
    <ContentLayout>
      <HeaderBackButton url="/approvals/responses" />

      <ApprovalTitle
        project_group_title={data.project_group_title}
        title={data.title}
        data={data}
        currentUser={currentUser}
        passedMyApproval={passedMyApproval}
        passedReceiveType={passedReceiveType}
        onApprovalClick={(type) => openDialog(type)}
      />

      <div className="grid gap-5">
        <div className="grid grid-cols-[400px_auto] gap-5">
          <Card title="승인 정보" icon={ProjectInfoIcon}>
            <ApprovalDetailInfo data={data} />
          </Card>
          <Card
            title="승인자 별 승인/반려 사유"
            icon={ManualProductionAnalysisIcon}
          >
            <ApprovalDetailReasons stages={data.stages} />
          </Card>
        </div>
        <Card title="시나리오 상세" icon={TableIcon}>
          <ApprovalDetailTable data={data} />
        </Card>
      </div>

      <ApprovalAlertDialog
        open={openAlert}
        onClose={() => {
          setOpenAlert(false);
        }}
        onSubmit={handleSubmit}
        type={
          dialogType as ScenarioDecision.APPROVED | ScenarioDecision.REJECTED
        }
      />
    </ContentLayout>
  );
}

const ApprovalTitle = ({
  project_group_title,
  title,
  data,
  currentUser,
  passedMyApproval,
  passedReceiveType,
  onApprovalClick,
}: {
  project_group_title: string;
  title: string;
  data: any;
  currentUser: any;
  passedMyApproval?: any;
  passedReceiveType?: string;
  onApprovalClick: (
    type: ScenarioDecision.APPROVED | ScenarioDecision.REJECTED
  ) => void;
}) => {
  const { current_stage, final_decision, my_approval, requested_by } = data;

  const effectiveMyApproval = passedMyApproval || my_approval;
  const effectiveReceiveType =
    passedReceiveType ||
    effectiveMyApproval?.receive_type ||
    APPROVAL_RECEIVE_TYPE.CC;

  const { stage = 0, decision } = effectiveMyApproval || {};

  const isUndecided = decision === ScenarioDecision.UNDECIDED;
  const isFinished = final_decision !== ScenarioDecision.UNDECIDED;
  const beforeMyTurn = current_stage < stage;

  const showButtons =
    !isFinished && effectiveReceiveType !== APPROVAL_RECEIVE_TYPE.CC;

  return (
    <div className="flex items-center justify-between w-full">
      <p className="text-gray-A text-lg font-bold flex-1">
        {project_group_title}
        <span className="text-gray-B">의 </span>
        {title}
      </p>

      {showButtons && (
        <div className="flex items-center gap-3 ml-4">
          <button
            className={cn(
              "py-1 px-2 flex text-white rounded-sm",
              decision === ScenarioDecision.APPROVED
                ? "bg-gray-D text-gray-B"
                : "bg-pink",
              beforeMyTurn && "cursor-not-allowed opacity-30"
            )}
            onClick={() => onApprovalClick(ScenarioDecision.REJECTED)}
            disabled={!isUndecided || beforeMyTurn || isFinished}
          >
            반려
          </button>
          <button
            className={cn(
              "py-1 px-2 flex text-white rounded-sm",
              decision === ScenarioDecision.REJECTED
                ? "bg-gray-D text-gray-B"
                : "bg-green",
              beforeMyTurn && "cursor-not-allowed opacity-30"
            )}
            onClick={() => onApprovalClick(ScenarioDecision.APPROVED)}
            disabled={!isUndecided || beforeMyTurn || isFinished}
          >
            승인
          </button>
        </div>
      )}
    </div>
  );
};
